import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { MealService } from "../core/services/meal.service";
import { BaseDateOption, generateDateOptions } from "../shared/utils/commenFunctions";
import { MealDataResponse, MealDataItem, MealPreference, UserDetails } from "../core/models/meal.model";
import { pageLoadAnimation } from "../shared/utils/common-animations";
import { getUserDetails, getProgramId } from "../shared/utils/storage-utils";

type DateCardOption = BaseDateOption;

@Component({
  selector: "select-meal-transport-component, SelectMealTransportComponent",
  standalone: true,
  templateUrl: './select-meal-transport.component.html',
  styleUrls: ['./select-meal-transport.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimation]
})
export class SelectMealTransportComponent implements OnInit { 
  pageLoadState = 'hidden';
  dateOptions: DateCardOption[] = [];
  isNavigating = false;
  showInfoMessage = true; // Flag to control info message visibility
  infoMessageType: 'none' | 'partial' | 'all' = 'none'; // Message type: none, partial (some dates missing), all (all dates selected)
  /** Senior Center: transport required only after transportation approval. */
  isTransportationApproved = getProgramId() !== 1000;
  constructor(
    private readonly router: Router,
    private readonly mealService: MealService
  ) {}
  userDetails: UserDetails = getUserDetails();

  get canShowTransport(): boolean {
    if (getProgramId() === 1000) {
      return this.isTransportationApproved;
    }
    return true;
  }
  onSelectMealTransport (): void {
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;
    this.getMealDataAndRedirect();
  }

  onBackClick(): void {
    this.router.navigate(['/card-checkin']);
  }

  ngOnInit(): void {
    this.dateOptions = generateDateOptions();
    this.checkForPrebookedMeals();
  }

  private checkForPrebookedMeals(): void {
    const dateValues = this.dateOptions.map(option => option.dateValue ?? '').filter(d => d);
    if (dateValues.length === 0) {
      this.showInfoMessage = true;
      this.infoMessageType = 'none';
      return;
    }

    const payload = {
      userId: this.userDetails.value,
      date: dateValues,
      programId: getProgramId(),
    };

    this.mealService.getMealInfo(payload).subscribe({
      next: (response: MealDataResponse) => {
        if (!response) {
          this.infoMessageType = 'none';
          this.showInfoMessage = true;
          return;
        }
        this.isTransportationApproved =
          getProgramId() === 1000
            ? (response as MealDataResponse & { isTransportationApproved?: boolean }).isTransportationApproved === true
            : true;
        const selectionState = this.getSelectionState(response);
        this.infoMessageType = selectionState;
        // Show message for all states (none, partial, all)
        this.showInfoMessage = true;
      },
      error: () => {
        // On error, show the info message by default with 'none' state
        this.showInfoMessage = true;
        this.infoMessageType = 'none';
      }
    });
  }

  private getSelectionState(mealData: MealDataResponse | MealDataItem[]): 'none' | 'partial' | 'all' {
    const datesWithFullSchedule = new Set<string>();
    const datesInApiResults = new Set<string>(); // Track which dates are in API response
    const allDateValues = this.dateOptions.map(option => option.dateValue ?? '').filter(d => d);
    const todayDateValue = this.dateOptions[0]?.dateValue ?? '';

    // Check if API returned empty or no results
    const mealDataAsResponse = Array.isArray(mealData) ? null : mealData;
    const hasResults = mealData && (
      (mealDataAsResponse?.results && Array.isArray(mealDataAsResponse.results) && mealDataAsResponse.results.length > 0) ||
      (Array.isArray(mealData) && mealData.length > 0)
    );

    // If no meal data or empty results, today is not selected - show 'none' message
    if (!hasResults) {
      return 'none';
    }

    // Use the same logic as evaluateNavigationFromMealData for consistency
    if (mealDataAsResponse?.results && Array.isArray(mealDataAsResponse.results)) {
      mealDataAsResponse.results.forEach((item: MealDataItem) => {
        if (item.mealDate) {
          datesInApiResults.add(item.mealDate); // Track that this date exists in API response
          
          if (Array.isArray(item.mealPreference)) {
            const hasNoMeal = item.mealPreference.some(
              (pref: MealPreference) => (pref.preference ?? '').trim().toLowerCase() === 'no meal'
            );
            const hasMeal = item.mealPreference.some((pref: MealPreference) => pref.preference);
            // Exclude "Not Required" / legacy "No Need" and "none" from transport check
            const hasTransport = item.mealPreference.some((pref: MealPreference) => 
              pref.transport && 
              pref.transport.trim() !== '' && 
              pref.transport !== 'none' && 
              pref.transport !== 'No Need' &&
              pref.transport !== 'Not Required' &&
              pref.transport !== 'Not required'
            );
            const hasTransportChoice = item.mealPreference.some(
              (pref: MealPreference) =>
                !!pref.transport && pref.transport.trim() !== '' && pref.transport !== 'none'
            );
            if (hasNoMeal || (hasMeal && (hasTransport || hasTransportChoice || !this.canShowTransport))) {
              datesWithFullSchedule.add(item.mealDate);
            }
          }
        }
      });
    } else if (Array.isArray(mealData)) {
      mealData.forEach((item: MealDataItem) => {
        const dateValue = item.mealDate ?? item.date;
        if (!dateValue) {
          return;
        }
        datesInApiResults.add(dateValue); // Track that this date exists in API response
        
        if (Array.isArray(item.mealPreference)) {
          const hasNoMeal = item.mealPreference.some(
            (pref: MealPreference) => (pref.preference ?? '').trim().toLowerCase() === 'no meal'
          );
          const hasMeal = item.mealPreference.some((pref: MealPreference) => pref.preference);
          // Exclude "Not Required" / legacy "No Need" and "none" from transport check
          const hasTransport = item.mealPreference.some((pref: MealPreference) => 
            pref.transport && 
            pref.transport.trim() !== '' && 
            pref.transport !== 'none' && 
            pref.transport !== 'No Need' &&
            pref.transport !== 'Not Required' &&
            pref.transport !== 'Not required'
          );
          const hasTransportChoice = item.mealPreference.some(
            (pref: MealPreference) =>
              !!pref.transport && pref.transport.trim() !== '' && pref.transport !== 'none'
          );
          if (hasNoMeal || (hasMeal && (hasTransport || hasTransportChoice || !this.canShowTransport))) {
            datesWithFullSchedule.add(dateValue);
          }
        }
      });
    }

    // Check how many dates have full schedule (both meal and transport)
    const scheduledCount = allDateValues.filter(date => datesWithFullSchedule.has(date)).length;
    
    // Check if today is in API results
    const todayInApiResults = todayDateValue && datesInApiResults.has(todayDateValue);
    
    // Return 'none' if today is not in API results AND no dates are fully selected
    if (!todayInApiResults && scheduledCount === 0) {
      return 'none';
    } else if (scheduledCount === allDateValues.length) {
      return 'all';
    } else {
      return 'partial';
    }
  }

  private hasAnyPrebookedMeals(mealData: MealDataResponse | MealDataItem[]): boolean {
    let mealItems: MealDataItem[] = [];

    if (!Array.isArray(mealData) && mealData?.results && Array.isArray(mealData.results)) {
      mealItems = mealData.results;
    } else if (Array.isArray(mealData)) {
      mealItems = mealData;
    }

    return mealItems.some((item: MealDataItem) => {
      // Check if submitted is true
      if (item.submitted === true) {
        return true;
      }

      // Check if mealPreference has any non-empty preferences
      if (Array.isArray(item.mealPreference)) {
        const hasNonEmptyPreference = item.mealPreference.some((pref: MealPreference) => {
          // Check if preference object has any meaningful values
          const hasPreference = pref.preference && pref.preference.trim() !== '';
          const hasDrinks = pref.drinks && pref.drinks.trim() !== '';
          const hasTransport = pref.transport && pref.transport.trim() !== '' && pref.transport !== 'No Need' && pref.transport !== 'Not Required' && pref.transport !== 'Not required';
          
          return hasPreference || hasDrinks || hasTransport;
        });
        
        return hasNonEmptyPreference;
      }

      return false;
    });
  }

  ngAfterViewInit(): void {
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }

  private getMealDataAndRedirect(): void {
    const dateValues = this.dateOptions.map(option => option.dateValue ?? '');
    const payload = {
      userId: this.userDetails.value,
      date: dateValues,
      programId: getProgramId(),
    };

    this.mealService.getMealInfo(payload).subscribe({
      next: (response: MealDataResponse) => {
        this.isTransportationApproved =
          getProgramId() === 1000
            ? (response as MealDataResponse & { isTransportationApproved?: boolean }).isTransportationApproved === true
            : true;
        const navigationResult = this.evaluateNavigationFromMealData(response);
        
        // Store current route so the destination component knows where to navigate back
        // Only store if navigating to success-meal-transport (other routes handle their own navigation)
        if (navigationResult[0] === '/success-meal-transport') {
          sessionStorage.setItem('previousRoute', this.router.url);
        }
        
        this.router.navigate(navigationResult);
      },
      error: () => {
        // If API fails, fall back to date selection flow
        this.router.navigate(['/date-selection']);
        this.isNavigating = false;
      },
      complete: () => {
        this.isNavigating = false;
      }
    });
  }

  private evaluateNavigationFromMealData(mealData: MealDataResponse | MealDataItem[]): string[] {
    const datesWithFullSchedule = new Set<string>();

    const markIfScheduled = (item: MealDataItem, dateValue: string): void => {
      if (!Array.isArray(item.mealPreference)) {
        return;
      }
      const hasNoMeal = item.mealPreference.some(
        (pref: MealPreference) => (pref.preference ?? '').trim().toLowerCase() === 'no meal'
      );
      const hasMeal = item.mealPreference.some((pref: MealPreference) => pref.preference);
      const hasTransportChoice = item.mealPreference.some(
        (pref: MealPreference) =>
          !!pref.transport && pref.transport.trim() !== '' && pref.transport !== 'none'
      );
      if (hasNoMeal || (hasMeal && (hasTransportChoice || !this.canShowTransport))) {
        datesWithFullSchedule.add(dateValue);
      }
    };

    if (!Array.isArray(mealData) && mealData?.results && Array.isArray(mealData.results)) {
      mealData.results.forEach((item: MealDataItem) => {
        if (item.mealDate) {
          markIfScheduled(item, item.mealDate);
        }
      });
    } else if (Array.isArray(mealData)) {
      mealData.forEach((item: MealDataItem) => {
        const dateValue = item.mealDate ?? item.date;
        if (!dateValue) {
          return;
        }
        markIfScheduled(item, dateValue);
      });
    }

    // Get today's date value (first date option)
    const todayDateValue = this.dateOptions[0]?.dateValue ?? '';
    
    // Get future date values (excluding today)
    const futureDateValues = this.dateOptions
      .filter((_, idx) => idx !== 0)
      .map(option => option.dateValue)
      .filter((value): value is string => Boolean(value));

    // Check if today is NOT booked but future dates ARE booked
    const todayIsNotBooked = todayDateValue && !datesWithFullSchedule.has(todayDateValue);
    const futureDatesAreBooked = futureDateValues.some(date => datesWithFullSchedule.has(date));
    
    // If today is not booked but tomorrow/future dates are already booked, redirect to date-selection
    if (todayIsNotBooked && futureDatesAreBooked) {
      return ['/date-selection'];
    }

    const allFutureDatesFullyScheduled =
      futureDateValues.length > 0 &&
      futureDateValues.every(date => datesWithFullSchedule.has(date));

    if (allFutureDatesFullyScheduled) {
      return ['/scheduled-confirmed'];
    }

    if (datesWithFullSchedule.size > 0) {
      return ['/success-meal-transport'];
    }

    return ['/date-selection'];
  }
}
